import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { PurchaseLotStockOutForm } from './new'
import type { CreatePurchaseOutNoteRequest } from '@/lib/api/purchase-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { purchaseLotApi } from '@/lib/api/purchase-lot-api'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    usePurchaseLots,
    usePurchaseOutNote,
    useUpdatePurchaseOutNote,
} from '@/hooks/use-purchase-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import {
    purchaseLotOption,
    purchaseNoteItemOption,
} from '@/components/lots/lot-option-builders'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-out-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = usePurchaseOutNote(id)
    const note = data?.data
    const update = useUpdatePurchaseOutNote()

    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch, 300)
    const { data: lotsData } = usePurchaseLots({
        per_page: 50,
        only_available: '1',
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
    const lotOptions = useMemo<LotOption[]>(
        () => (lotsData?.data ?? []).map(purchaseLotOption),
        [lotsData],
    )

    // Lô đang có trong phiếu có thể ngoài top 50 / đã hết available → seed để
    // Combobox hiện đúng nhãn.
    const noteLotSeeds = useMemo<LotOption[]>(
        () => (note?.items ?? []).map(purchaseNoteItemOption),
        [note],
    )

    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await purchaseLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, purchaseLotOption(l))
                return next
            })
        } catch {
            // Bỏ qua — row vẫn giữ ID trong form state.
        }
    }

    const mergedLotOptions = useMemo<LotOption[]>(() => {
        const map = new Map<string, LotOption>()
        for (const opt of noteLotSeeds) map.set(opt.value, opt)
        for (const opt of lotOptions) map.set(opt.value, opt)
        for (const opt of scannedLots.values()) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [noteLotSeeds, lotOptions, scannedLots])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/purchase-lots/stock-out-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const initialValues = {
        reason: note.reason,
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            purchase_lot_id: it.purchase_lot_id,
            quantity: it.quantity,
        })),
    }

    const backToDetail = () =>
        navigate({ to: '/purchase-lots/stock-out-notes/$id', params: { id } })

    const handleSubmit = async (value: CreatePurchaseOutNoteRequest) => {
        try {
            await update.mutateAsync({
                id,
                req: {
                    reason: value.reason,
                    notes: value.notes,
                    items: value.items,
                },
            })
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PurchaseLotStockOutForm
            mode="edit"
            title={m.pl_pxk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            purchaseLotOptions={mergedLotOptions}
            isPending={update.isPending}
            onLotSearchChange={setLotSearch}
            onLotScanned={(lotId) => void fetchAndCacheLot(lotId)}
            onCancel={() => void backToDetail()}
            onSubmit={(value) => void handleSubmit(value)}
        />
    )
}
